import { CloseFill18Icon } from "@vygruppen/spor-icon-react";
import { StretchNameBadges } from "features/CenterContent/RoleContent/EventInteraction/StretchNameBadges";
import { GlobalEventModal } from "features/CenterContent/RoleContent/GlobalEvents/GlobalEventModal";
import CreateInternalMessageModal from "features/CenterContent/RoleContent/InternalMessage/CreateInternalMessageModal";
import { CreateProductionDeviationModal } from "features/CenterContent/RoleContent/ProductionDeviation/CreateProductionDeviationModal";
import { stopNameWithFallback } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/generateStretchName";
import {
  getClickedStopPairs,
  getStretchName,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stretchBuilderTexts";
import { FC, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ButtonGroup,
  FilterChip,
  DropsButton,
  Flex,
  HStack,
  Text,
} from "shared/components/Spor";
import { VerticalSeparator } from "shared/components/VerticalSeparators";
import { useDropsRole } from "stores/useDropsRole";
import { useStretchBuilder } from "stores/useStretchBuilder";
import styled, { useTheme } from "styled-components";

const AnimatedBox = styled.div<{ show: boolean }>`
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.3s;
`;

const CloseButton = styled(DropsButton)`
  &&& {
    border-radius: 0.4rem !important;
    padding: 0;
  }
`;

type EventTypeButton = {
  title: string;
  disabled: boolean;
  onClick: () => void;
  hasSeparator?: boolean;
};

export const EventInteractionBar: FC = () => {
  const theme = useTheme();
  const [showGlobalEventModal, setShowGlobalEventModal] = useState(false);
  const [showInternalMessageModal, setShowInternalMessageModal] =
    useState(false);
  const [showProductionDeviationModal, setShowProductionDeviationModal] =
    useState(false);

  const [show, setShow] = useState(false);

  const { isDriftledningen } = useDropsRole();
  const { t } = useTranslation();

  useEffect(() => {
    setTimeout(() => {
      setShow(true);
    }, 50);
  }, []);

  const [
    clickedStops,
    selectedLegs,
    selectedStops,
    allStopsSelected,
    setAllStopsSelected,
    stretchBuilderModalState,
    setStretchBuilderModalState,
    closeAndResetStretchBuilder,
    setSelectedLegs,
    setClickedStops,
    setSelectedStops,
  ] = useStretchBuilder((state) => [
    state.clickedStops,
    state.selectedLegs,
    state.selectedStops,
    state.allStopsSelected,
    state.setAllStopsSelected,
    state.stretchBuilderModalState,
    state.setStretchBuilderModalState,
    state.closeAndResetStretchBuilder,
    state.setSelectedLegs,
    state.setClickedStops,
    state.setSelectedStops,
  ]);

  const { singleClickedStop } = getClickedStopPairs(clickedStops);

  const isEditStation =
    stretchBuilderModalState.type === "station" &&
    !!stretchBuilderModalState.eventInfo;
  const isEditInfrastructure =
    stretchBuilderModalState.type === "infrastructure" &&
    !!stretchBuilderModalState.eventInfo;
  const disableInfrastructureButton =
    isEditStation || selectedLegs.length === 0 || singleClickedStop !== null;
  const disableStationButton =
    isEditInfrastructure || selectedStops.length === 0;
  const hasSelectedStops = selectedStops.length > 0;

  const eventTypeButtons: EventTypeButton[] = [
    {
      title: t("Infrastruktur"),
      disabled: disableInfrastructureButton,
      onClick: () => {
        if (stretchBuilderModalState.type === "infrastructure") {
          // if type is already "infrastructure", it means edit mode
          setStretchBuilderModalState({
            ...stretchBuilderModalState,
            stretchBuilderMode: false,
          });
        } else {
          setStretchBuilderModalState({
            type: "infrastructure",
            stretchBuilderMode: false,
          });
        }
      },
    },
    {
      title: t("Stasjon"),
      disabled: disableStationButton,
      onClick: () => {
        if (stretchBuilderModalState.type === "station") {
          setStretchBuilderModalState({
            ...stretchBuilderModalState,
            stretchBuilderMode: false,
          });
        } else {
          setStretchBuilderModalState({
            type: "station",
            stretchBuilderMode: false,
          });
        }
      },
    },
    {
      title: t("Global driftsmelding"),
      disabled: hasSelectedStops,
      onClick: () => {
        setShowGlobalEventModal(true);
        setStretchBuilderModalState({
          type: "none",
        });
      },
      hasSeparator: true,
    },
    {
      title: t("Intern melding"),
      disabled: false,
      onClick: () => {
        if (selectedStops.length === 0) {
          setStretchBuilderModalState({
            type: "none",
          });
        }
        setShowInternalMessageModal(true);
      },
    },
    {
      title: t("Produksjonsavvik"),
      disabled: hasSelectedStops,
      onClick: () => {
        setShowProductionDeviationModal(true);
        setStretchBuilderModalState({
          type: "none",
        });
      },
      hasSeparator: true,
    },
  ];

  const eventTypeButtonsToShow = eventTypeButtons.filter((button) => {
    if (button.title === t("Intern melding")) return !isDriftledningen();
    if (button.title === t("Produksjonsavvik")) return !isDriftledningen();
    if (button.title === t("Global driftsmelding")) return !isDriftledningen();
    return true;
  });

  const stretchNames = isEditStation
    ? selectedStops
        .map((stop) => stopNameWithFallback(stop))
        .filter((stop) => stop !== singleClickedStop?.name)
    : getStretchName(selectedLegs, clickedStops, allStopsSelected);

  const removeStations = () => {
    setClickedStops([]);
    setSelectedLegs([]);
    setAllStopsSelected(false);
    setSelectedStops([]);
  };

  return (
    <AnimatedBox show={show}>
      <Flex
        backgroundColor={theme.trainMapStretchBuilder}
        borderColor={theme.colorSuccessOutline}
        borderWidth="1px 1px 0 1px"
        color={theme.colorSuccessTextMain}
        justifyContent="stretch"
        paddingY="12px"
        paddingX="18px"
        borderRadius="6px 6px 0 0"
        pointerEvents="auto"
      >
        <HStack width="100%" flexWrap="wrap">
          <Text
            variant="sm"
            fontWeight="bold"
            width="fit-content"
            paddingRight="12px"
          >
            {isEditInfrastructure || isEditStation ? t("Endre") : t("Opprett")}{" "}
            {t("hendelse")}
          </Text>
          <Text variant="sm" width="fit-content">
            {t("For område")}
          </Text>
          {!isDriftledningen() && (
            <FilterChip
              size="xs"
              checked={allStopsSelected}
              onCheckedChange={() => setAllStopsSelected(!allStopsSelected)}
            >
              <Text variant="sm">{t("Hele landet")}</Text>
            </FilterChip>
          )}
          {!allStopsSelected && (
            <StretchNameBadges
              stretchNames={stretchNames}
              singleClickedStop={singleClickedStop}
            />
          )}
          {selectedStops.length > 0 && !allStopsSelected && (
            <CloseButton
              size="xs"
              semantics="success"
              disabled={false}
              onClick={() => removeStations()}
            >
              <CloseFill18Icon />
            </CloseButton>
          )}

          <HStack flexWrap="nowrap">
            <Text variant="sm" minWidth="fit-content">
              {t("og type hendelse")}
            </Text>
            <ButtonGroup>
              {eventTypeButtonsToShow.map((button) => (
                <Fragment key={button.title}>
                  {button.hasSeparator && <VerticalSeparator />}
                  <DropsButton
                    size="xs"
                    semantics="success"
                    onClick={button.onClick}
                    variant="tertiary"
                    disabled={button.disabled}
                  >
                    {button.title}
                  </DropsButton>
                </Fragment>
              ))}
            </ButtonGroup>
          </HStack>
        </HStack>
        <HStack alignItems="start">
          <DropsButton
            size="xs"
            semantics="success"
            onClick={() => closeAndResetStretchBuilder()}
            disabled={false}
            leftIcon={<CloseFill18Icon />}
            variant="tertiary"
          >
            {t("Avbryt")}
          </DropsButton>
        </HStack>
      </Flex>

      {showGlobalEventModal && (
        <GlobalEventModal onClose={() => closeAndResetStretchBuilder()} />
      )}

      {showInternalMessageModal && (
        <CreateInternalMessageModal
          setModalOpen={setShowInternalMessageModal}
          stations={selectedStops}
          allStopsSelected={allStopsSelected}
          stretchBuilderModalState={stretchBuilderModalState}
          setStretchBuilderModalState={setStretchBuilderModalState}
          onClose={() => {
            closeAndResetStretchBuilder();
          }}
        />
      )}

      {showProductionDeviationModal && (
        <CreateProductionDeviationModal
          onClose={() => closeAndResetStretchBuilder()}
        />
      )}
    </AnimatedBox>
  );
};
